import { Box, Skeleton } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getProjectVariablePath } from '@verantos-dev/core-constants';
import { ColumnsOnlyToolbar, DataGrid, DataGridLayout } from '@verantos-dev/shared-components';

import type { AnalyticalDatasetResultRow } from '../../api/types';
import { VariablesTitle } from '../../components/AnalyticalVariablesControls';
import { useVariablePreviewRenderer } from '../../context/VariablePreviewContext';
import {
  useAnalyticalDatasetResults,
  type PendingResultsColumn,
  type PlaceholderResultsColumn,
} from '../../hooks/useAnalyticalDatasetResults';
import { useAvPreview } from '../../hooks/useAvPreview';
import { useVariableSourceList } from '../../hooks/useVariableSourceList';
import type { ResultsColumnLink, SelectedDatasetSource } from '../../types';
import {
  RESULTS_DEFAULT_SORT_MODEL,
  RESULTS_INITIAL_PAGE_SIZE,
  RESULTS_PINNED_COLUMNS,
} from '../../utils/datasetResultsGrid';
import { toPlaceholderColumnType } from '../../utils/placeholderColumnType';

interface AnalyticalDatasetResultsProps {
  projectId: string;
  /** "Variables (N)", shown in the grid's toolbar. */
  variablesTitle: string;
  analyticalDatasetProfileId: string;
  /** Whether the dataset's definition has any analytical variable; the
   * demographic columns are hidden by default when it does. */
  hasAnalyticalVariables?: boolean;
  /** Every source of the definition (saved or staged); the ones the run has a
   * column for supply the header row shown while the results load. */
  datasetSources?: SelectedDatasetSource[];
  /** Definition sources (saved or staged) the processed run has no column
   * for; each is appended as an empty column. */
  sourcesMissingFromRun?: SelectedDatasetSource[];
}

const NO_SOURCES: SelectedDatasetSource[] = [];

export const AnalyticalDatasetResults = ({
  projectId,
  variablesTitle,
  analyticalDatasetProfileId,
  hasAnalyticalVariables = false,
  datasetSources = NO_SOURCES,
  sourcesMissingFromRun = NO_SOURCES,
}: AnalyticalDatasetResultsProps) => {
  const { formatMessage } = useIntl();
  const { rows, isLoading } = useVariableSourceList(datasetSources);
  const renderVariablePreview = useVariablePreviewRenderer();
  const { previewAvId, onOpenPreview, onClosePreview } = useAvPreview();

  // A variable column header opens that variable: a plain click shows its definition
  // popup, a modified click follows the link to its page in a new tab. Without a
  // popup renderer (a host that does not inject one) headers stay plain text, as the
  // table headers do.
  const getVariableLink = useCallback(
    (avId: string): ResultsColumnLink | null =>
      renderVariablePreview
        ? { href: getProjectVariablePath(projectId, avId), onOpen: () => onOpenPreview(avId) }
        : null,
    [renderVariablePreview, projectId, onOpenPreview]
  );

  // The server keys its columns by a slug of the variable's name, not its id, so a
  // column is matched to its variable by id (the header placeholders) or by display
  // name. A name shared by two variables is left unlinked rather than guessed.
  const avIdByColumn = useMemo(() => {
    const byId = new Set<string>();
    const byName = new Map<string, string | null>();
    rows.forEach((row) => {
      if (row.status === 'default' || row.avId === null) return;
      byId.add(row.avId);
      byName.set(row.title, byName.has(row.title) ? null : row.avId);
    });
    return { byId, byName };
  }, [rows]);

  const getColumnVariableLink = useCallback(
    ({ field, headerName }: { field: string; headerName: string }): ResultsColumnLink | null => {
      const avId = avIdByColumn.byId.has(field) ? field : avIdByColumn.byName.get(headerName);
      return avId ? getVariableLink(avId) : null;
    },
    [avIdByColumn, getVariableLink]
  );

  const { pendingColumns, placeholderColumns } = useMemo(() => {
    const missingKeys = new Set(sourcesMissingFromRun.map((source) => source.key));
    const sourceRows = rows.filter((row) => row.status !== 'default');
    return {
      pendingColumns: sourceRows
        .filter((row) => missingKeys.has(row.key))
        .map<PendingResultsColumn>((row) => ({
          key: row.key,
          title: isLoading && row.avId !== null ? '' : row.title,
          link: row.avId === null ? null : getVariableLink(row.avId),
        })),
      // A variable the run resolved has a real column; showing its header up
      // front keeps the grid from growing from the system columns to the full
      // set when the first response lands.
      placeholderColumns: sourceRows
        .filter((row) => !missingKeys.has(row.key))
        .flatMap<PlaceholderResultsColumn>((row) =>
          row.avId === null
            ? []
            : [
                {
                  field: row.avId,
                  headerName: row.title,
                  type: toPlaceholderColumnType(row.typeLabel),
                },
              ]
        ),
    };
  }, [rows, isLoading, sourcesMissingFromRun, getVariableLink]);

  const {
    mapServerColumns,
    useFetchResultsGridData,
    columnVisibilityModel,
    onColumnVisibilityModelChange,
  } = useAnalyticalDatasetResults({
    projectId,
    analyticalDatasetProfileId,
    hasAnalyticalVariables,
    placeholderColumns,
    pendingColumns,
    pendingCaption: formatMessage({ id: 'analysisDetail.tables.preview.resultsColumnPending' }),
    getVariableLink: getColumnVariableLink,
  });

  // Variable names come from the lookup; until it resolves, a variable's header
  // (placeholder or pending) would be a raw id or blank, and the grid would mount
  // with only the system columns and grow when the names land. Hold a skeleton
  // instead of mounting it, as the analytical dataset page does.
  const hasUnnamedVariableColumns = rows.some(
    (row) => row.status !== 'default' && row.avId !== null
  );
  if (isLoading && hasUnnamedVariableColumns) {
    return (
      <Skeleton
        variant="rounded"
        animation="wave"
        sx={{ width: '100%', flex: 1, minHeight: 400 }}
        data-testid="analysis-results-dataset-grid-loading"
      />
    );
  }

  return (
    <Box
      // minHeight matches DataGridLayout's floor so a short viewport scrolls the
      // page instead of squashing the grid.
      sx={{ width: '100%', flex: 1, minHeight: 400, display: 'flex', flexDirection: 'column' }}
      data-testid="analysis-results-dataset-grid"
    >
      {/* No maxHeight: the layout flexes to its container's height (its own
          400px minHeight stays as the floor) and the grid scrolls inside. */}
      <DataGridLayout id="analysis-results-dataset-grid" sx={{ width: '100%', flex: 1 }}>
        <DataGrid
          id="analysis-results-dataset-grid"
          columns={mapServerColumns}
          getRowId={(row: AnalyticalDatasetResultRow) => String(row.patient_id)}
          mode="server"
          useFetchDataGridData={useFetchResultsGridData}
          getRowCount={(data) => data.pagination.total}
          pagination
          loadOnFetch
          disableSearchParamSync
          // Opt-in shared-grid behaviors for server-side filtering.
          // Multi-column filtering stays on — the backend ANDs/ORs all
          // comma-joined `field:operator(value)` items.
          resetPageOnFilterChange
          serializeDateFilterValues
          initialPageSize={RESULTS_INITIAL_PAGE_SIZE}
          pageSizeOptions={[25, 50, 100, 250, 500]}
          initialState={{
            sorting: { sortModel: RESULTS_DEFAULT_SORT_MODEL },
            pinnedColumns: RESULTS_PINNED_COLUMNS,
          }}
          // Demographic columns are hidden by default when the dataset has
          // analytical variables; toggles from the columns panel are kept in
          // the hook.
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={onColumnVisibilityModelChange}
          // Boolean filter Value dropdown labels, matching the title-cased
          // cell rendering (True/False). Display-only — the filter item value
          // (and thus the serialized wire format) stays true/false.
          localeText={{
            filterValueAny: 'Any',
            filterValueTrue: 'True',
            filterValueFalse: 'False',
          }}
          slots={{ toolbar: ColumnsOnlyToolbar }}
          slotProps={{
            toolbar: { startContent: <VariablesTitle title={variablesTitle} /> },
            baseSelect: {
              MenuProps: {
                PaperProps: { sx: { maxHeight: 320 } },
              },
            },
            panel: {
              sx: {
                // Filter panel: line the rows up as a table — consistent field
                // widths across rows (the hidden And/Or slot on row 1 already
                // reserves its column), padded rows, aligned baselines.
                '& .MuiDataGrid-filterForm': {
                  padding: 3,
                  gap: 3,
                  alignItems: 'flex-end',
                },
                '& .MuiDataGrid-filterFormLogicOperatorInput': { minWidth: 64 },
                '& .MuiDataGrid-filterFormColumnInput': { width: 200 },
                '& .MuiDataGrid-filterFormOperatorInput': { width: 150 },
                '& .MuiDataGrid-filterFormValueInput': { width: 200 },
                '& .MuiDataGrid-panelFooter': {
                  paddingLeft: 3,
                  paddingRight: 3,
                  paddingBottom: 2,
                },
                '& .MuiDataGrid-columnsManagementHeader': {
                  paddingLeft: 5,
                  paddingRight: 5,
                  paddingTop: 4,
                },
                '& .MuiDataGrid-columnsManagement': { paddingLeft: 5, paddingRight: 5 },
                '& .MuiDataGrid-columnsManagementRow': { marginLeft: 0, marginRight: 0 },
                '& .MuiDataGrid-columnsManagementRow .MuiCheckbox-root': { paddingLeft: 0 },
                '& .MuiDataGrid-columnsManagementFooter .MuiFormControlLabel-root': {
                  marginLeft: 0,
                },
                '& .MuiDataGrid-columnsManagementFooter .MuiCheckbox-root': { paddingLeft: 0 },
                '& .MuiDataGrid-columnsManagementFooter': {
                  paddingLeft: 5,
                  paddingRight: 5,
                  paddingTop: 4,
                  paddingBottom: 4,
                },
              },
            },
          }}
        />
      </DataGridLayout>
      {renderVariablePreview?.({ avId: previewAvId, onClose: onClosePreview })}
    </Box>
  );
};
